import { dayIn, isoDay } from "@colossus/contracts/analytics-scope";
import { perReader } from "./reader.ts";

export { dayIn };

const pinned = perReader((): { day: string | null } => ({ day: null }));

export const pinToday = (day: string): void => {
  pinned().day = day;
};

export const todayIn = (timeZone: string | null): string =>
  pinned().day ?? dayIn(new Date(), timeZone);

export interface DaySelection {
  readonly since: string | null;
  readonly until: string | null;
}

export const pickDay = (selection: DaySelection, day: string): DaySelection => {
  if (selection.since === null || selection.until !== null) return { since: day, until: null };
  if (day < selection.since) return { since: day, until: selection.since };
  return { since: selection.since, until: day };
};

export const isDay = (value: string): boolean => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const at = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(at.getTime()) && isoDay(at) === value;
};

export type TypedDayProblem = "format" | "future" | "order";

interface TypedRangeProblems {
  readonly since: TypedDayProblem | null;
  readonly until: TypedDayProblem | null;
}

export const typedRangeProblems = (
  since: string,
  until: string,
  today: string,
): TypedRangeProblems => {
  const own = (value: string): TypedDayProblem | null => {
    if (value === "") return null;
    if (!isDay(value)) return "format";
    return value > today ? "future" : null;
  };
  const start = own(since);
  const end = own(until);
  const reversed = start === null && end === null && since !== "" && until !== "" && until < since;
  return { since: start, until: reversed ? "order" : end };
};

export const isEdge = (selection: DaySelection, day: string): boolean =>
  selection.since === day || selection.until === day;

export const isInside = (selection: DaySelection, day: string): boolean =>
  selection.since !== null &&
  selection.until !== null &&
  day > selection.since &&
  day < selection.until;
